import { Routes } from '@angular/router';

import { LoginComponent }          from './core/login';
import { ForgotPasswordComponent } from './core/forgot-password';
import { ResetPasswordComponent }  from './core/reset-password';
import { TemporaryLoginComponent } from './core/temporary-login';
import { PageNotFoundComponent }   from './core/page-not-found';
import { HelpComponent }           from './core/help';
import { EnvironmentInfoComponent } from './core/environment-info';

import { AuthGuard } from '@app/_guards';
import { AtLeastAdminGuard } from '@app/_guards/at-least-admin.guard';

import * as routes from './routes';
import { DASHBOARD_ROUTES } from './core/dashboard/dashboard.routes';
import { REGISTRATION_PLATFORM_ROUTES } from './modules/registration-platform/registration-platform.routes';
import { STATISTICS_ROUTES } from './modules/statistics/statistics.routes';
import { PROJECT_ROUTES } from './modules/project/project.routes';
import { CAMPAIGN_ROUTES } from './modules/campaign/campaign.routes';
import { TEMPLATE_GENERATOR_ROUTES } from './modules/template-generator/template-generator.routes';
import { CLIENT_ROUTES } from './modules/client/client.routes';
import { PACKAGES_ROUTES } from './modules/packages/packages.routes';

export const appRoutes: Routes = [
  // Home (`''`) comes first in ROOT_ROUTES, so the features served from the root cannot take `/`.
  {
    path     : 'login',
    component: LoginComponent
  },
  {
    path     : 'help',
    component: HelpComponent
  },
  {
    path     : 'environment-info',
    component: EnvironmentInfoComponent,
    // Snöball staff only; the guard evaluates data.permission against the navigation policy.
    canActivate: [AuthGuard, AtLeastAdminGuard],
    data: {permission: 'open-environment-info'}
  },
  {
    path     : 'temporary-login',
    component: TemporaryLoginComponent
  },
  {
    path     : 'password',
    component: ForgotPasswordComponent
  },
  {
    path     : 'resetpassword',
    component: ResetPasswordComponent
  },
  {
    path        : 'front',
    loadChildren: () => import('./routes/front-route/front-route.routes').then(m => m.FRONT_ROUTES)
  },
  {
    path        : 'projects/:projectId/statistics',
    loadChildren: () => import('./modules/statistics/statistics.routes').then(m => m.STATISTICS_ROUTES)
  },
  {
    path        : 'users',
    loadChildren: () => import('./modules/user/user.routes').then(m => m.USER_ROUTES)
  },
  {
    path        : 'projects',
    loadChildren: () => import('./modules/project/project.routes').then(m => m.PROJECT_ROUTES)
  },
  {
    path        : 'project',
    loadChildren: () => import('./modules/project/project.routes').then(m => m.PROJECT_ROUTES)
  },
  {
    path        : 'projects/:projectId/influencers',
    loadChildren: () => import('./modules/influencer/influencer.routes').then(m => m.INFLUENCER_ROUTES)
  },
  // Bookmarks for the old Templates page, which Template Management replaced.
  { path: 'system-admin/templates', pathMatch: 'full', redirectTo: 'template-management' },
  {
    path        : 'template-management',
    loadChildren: () => import('./modules/template-generator/template-management/template-management.routes').then(m => m.TEMPLATE_MANAGEMENT_ROUTES)
  },
  {
    path        : 'data-protection',
    loadChildren: () => import('./modules/data-protection/data-protection.routes').then(m => m.DATA_PROTECTION_ROUTES)
  },
  {
    path        : 'projects/:id/campaigns',
    loadChildren: () => import('./modules/campaign/campaign.routes').then(m => m.CAMPAIGN_ROUTES)
  },
  {
    // TODO: figure out why routes.campaignTemplates({projectId: ':projectId', campaignId: ':campaignId'})
    // doesn't work here
    path        : 'projects/:projectId/campaigns/:campaignId',
    loadChildren: () => import('./modules/template-generator/template-generator.routes').then(m => m.TEMPLATE_GENERATOR_ROUTES)
  },
  {
    path        : 'clients',
    loadChildren: () => import('./modules/client/client.routes').then(m => m.CLIENT_ROUTES)
  },
  {
    path        : 'organizations',
    loadChildren: () => import('./modules/organization/organization.routes').then(m => m.ORGANIZATION_ROUTES)
  },
  {
    path        : 'organization',
    loadChildren: () => import('./modules/organization/organization.routes').then(m => m.ORGANIZATION_ROUTES)
  },
  {
    path        : 'projects/:projectId/segments',
    loadChildren: () => import('./modules/segment/segment.routes').then(m => m.SEGMENT_ROUTES)
  },
  {
    path        : 'system-admin',
    loadChildren: () => import('./modules/system-admin/system-admin.routes').then(m => m.SYSTEM_ADMIN_ROUTES)
  },
  {
    path: 'admin/bunny-cdn-overview',
    redirectTo: 'system-admin/cdn-monitoring',
    pathMatch: 'full'
  },
  {
    path: 'admin/cdn-monitoring',
    redirectTo: 'system-admin/cdn-monitoring',
    pathMatch: 'full'
  },
  // Bookmarks for pages moved from /settings/* to /system-admin/* by the design-system port.
  { path: 'settings', pathMatch: 'full', redirectTo: 'system-admin/settings' },
  { path: 'settings/add', pathMatch: 'full', redirectTo: 'system-admin/settings/add' },
  { path: 'settings/edit/:id', pathMatch: 'full', redirectTo: 'system-admin/settings/edit/:id' },
  { path: 'settings/cache', pathMatch: 'full', redirectTo: 'system-admin/cache' },
  { path: 'settings/global-stats', pathMatch: 'full', redirectTo: 'system-admin/global-stats' },
  { path: 'settings/rendering-system', pathMatch: 'full', redirectTo: 'system-admin/rendering-system' },
  { path: 'settings/ai-avatar-monitoring', pathMatch: 'full', redirectTo: 'system-admin/ai-avatar-monitoring' },
  { path: 'settings/task-queues', pathMatch: 'full', redirectTo: 'system-admin/task-queues' },
  { path: 'settings/social-share-monitoring', pathMatch: 'full', redirectTo: 'system-admin/social-share-monitoring' },
  { path: 'settings/packages', pathMatch: 'full', redirectTo: 'system-admin/packages' },
  { path: 'settings/packages/add', pathMatch: 'full', redirectTo: 'system-admin/packages/add' },
  { path: 'settings/packages/edit/:id', pathMatch: 'full', redirectTo: 'system-admin/packages/edit/:id' },
  { path: 'service-providers', pathMatch: 'full', redirectTo: 'system-admin/service-providers' },
  { path: 'templates', pathMatch: 'full', redirectTo: 'template-management' },
  {
    path        : 'settings',
    loadChildren: () => import('./modules/settings/settings.routes').then(m => m.SETTINGS_ROUTES)
  },
  {
    path        : 'registration-platform',
    loadChildren: () => import('./modules/registration-platform/registration-platform.routes').then(m => m.REGISTRATION_PLATFORM_ROUTES)
  },
  {
    path        : 'packages',
    loadChildren: () => import('./modules/registration-platform/registration-platform.routes').then(m => m.REGISTRATION_PLATFORM_ROUTES)
  },
  {
    path        : 'domains',
    loadChildren: () => import('./modules/mailer-domain/mailer-domain.routes').then(m => m.MAILER_DOMAIN_ROUTES)
  },
  {
    path        : 'api-access',
    loadChildren: () => import('./modules/api-access/api-access.routes').then(m => m.API_ACCESS_ROUTES)
  },
  // Old links to where these pages used to live. The /users/integration redirect lives in
  // the user routing module, which owns that path.
  {
    path        : 'api-keys',
    redirectTo  : 'api-access',
    pathMatch   : 'full'
  },
  {
    path        : 'api-keys/add',
    redirectTo  : 'api-access/add',
    pathMatch   : 'full'
  },
  {
    path        : 'system-admin/api-keys',
    redirectTo  : 'api-access',
    pathMatch   : 'full'
  },
  {
    path        : 'system-admin/api-keys/add',
    redirectTo  : 'api-access/add',
    pathMatch   : 'full'
  },
  // The two-factor setup notice sent on 15 October 2026 linked here.
  {
    path        : 'profile/security',
    redirectTo  : 'users/security',
    pathMatch   : 'full'
  },
  {
    path        : 'influencer-sso',
    loadChildren: () => import('./modules/influencer-sso/influencer-sso.routes').then(m => m.INFLUENCER_SSO_ROUTES)
  },
  {
    path        : 'global-widgets',
    loadChildren: () => import('./modules/global-widget/global-widget.routes').then(m => m.GLOBAL_WIDGET_ROUTES)
  },
  {
    path        : 'system-admin/service-providers',
    loadChildren: () => import('./modules/mailer-service-provider/mailer-service-provider.routes').then(m => m.MAILER_SERVICE_PROVIDER_ROUTES)
  },
  {
    path        : 'logs',
    loadChildren: () => import('./modules/logs/logs.routes').then(m => m.LOGS_ROUTES)
  },
  {
    path        : 'onboarding-form',
    loadChildren: () => import('./modules/onboarding-form/onboarding-form.routes').then(m => m.ONBOARDING_FORM_ROUTES)
  },
  {
    path     : '**',
    component: PageNotFoundComponent
  }
];

/**
 * Every route the panel registers at its root, in the order the router matches them: home first, so
 * no feature's `''` route can take `/`, then the features served from the root (they register their
 * own full paths; the lazy routes above load the same features under their prefixes), then the
 * pages above, ending with the not-found page.
 */
export const ROOT_ROUTES: Routes = [
  ...DASHBOARD_ROUTES,
  ...REGISTRATION_PLATFORM_ROUTES,
  ...STATISTICS_ROUTES,
  ...PROJECT_ROUTES,
  ...CAMPAIGN_ROUTES,
  ...TEMPLATE_GENERATOR_ROUTES,
  ...CLIENT_ROUTES,
  ...PACKAGES_ROUTES,
  ...appRoutes,
];
